import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';

import { PublicLayout } from './PublicLayout';

import { Button } from '@/components/ui/button';

const FAQ: { pergunta: string; resposta: string }[] = [
  {
    pergunta: 'O que é o JurisApp?',
    resposta:
      'É uma plataforma de produtividade jurídica para advogados e escritórios: gestão e consulta de processos, organização de comunicações dos tribunais, documentos, peticionamento eletrônico e assinatura digital — tudo em um só lugar.',
  },
  {
    pergunta: 'Como começo a usar?',
    resposta:
      'Crie sua conta e siga o onboarding. Há um período de avaliação para você conhecer a plataforma. Os planos e condições são apresentados no momento da contratação, na área de assinatura.',
  },
  {
    pergunta: 'O JurisApp consulta processos de quais tribunais?',
    resposta:
      'A consulta processual é integrada por meio da PDPJ, e as comunicações são recebidas a partir do DJEN, organizadas automaticamente por processo. A cobertura depende da disponibilidade desses sistemas oficiais.',
  },
  {
    pergunta: 'Preciso de certificado digital?',
    resposta:
      'Para assinar documentos e peticionar é necessário um certificado digital válido (e-CPF/A1). Seu certificado é criptografado e usado apenas para assinar em seu nome quando você peticiona — você decide quando cadastrá-lo e pode desativá-lo quando quiser. Tecnicamente, ele é cifrado com envelope encryption (chave mestra no AWS KMS) e a senha é protegida com AES-256-GCM, nunca armazenada em texto; é decifrado no servidor apenas no momento da assinatura.',
  },
  {
    pergunta: 'Como funciona o peticionamento?',
    resposta:
      'Você monta a petição com seus anexos, a plataforma realiza a assinatura digital e o envio com acompanhamento de cada etapa. Atos cujo resultado dependa do tribunal devem sempre ser conferidos no portal oficial.',
  },
  {
    pergunta: 'Meus dados estão seguros?',
    resposta:
      'Sim. Adotamos criptografia, controle de acesso por perfil e trilhas de auditoria. O tratamento de dados segue a LGPD — veja a Política de Privacidade para detalhes.',
  },
  {
    pergunta: 'Funciona offline?',
    resposta:
      'A plataforma é um aplicativo web instalável (PWA) com suporte offline: páginas e documentos já acessados ficam disponíveis, e ações feitas sem conexão são sincronizadas assim que a internet volta. Login e pagamento exigem conexão.',
  },
  {
    pergunta: 'Como cancelo minha assinatura?',
    resposta:
      'O cancelamento pode ser feito na área de assinatura. Você mantém o acesso até o fim do período já pago, e seus dados são preservados conforme a Política de Privacidade.',
  },
  {
    pergunta: 'Ainda tenho dúvidas. Como falo com vocês?',
    resposta:
      'Use a página de contato para enviar sua mensagem. Nossa equipe responde o mais rápido possível.',
  },
];

function FaqPage() {
  return (
    <PublicLayout>
      {/* Hero */}
      <div className="bg-background pb-10 pt-24 md:pt-32">
        <div className="container mx-auto max-w-screen-lg px-4">
          <span className="text-xs font-medium uppercase tracking-[0.15em] text-primary">FAQ</span>
          <h1 className="mb-4 mt-1 text-3xl font-bold text-foreground">Perguntas Frequentes</h1>
          <p className="max-w-[640px] text-base text-muted-foreground">
            Respostas rápidas para as principais dúvidas sobre o JurisApp.
          </p>
        </div>
      </div>

      {/* Lista */}
      <div className="container mx-auto max-w-screen-md px-4 pb-12">
        <div className="flex flex-col gap-3">
          {FAQ.map((item) => (
            <details key={item.pergunta} className="group rounded-lg border border-border bg-card">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5 text-sm font-semibold text-card-foreground [&::-webkit-details-marker]:hidden">
                {item.pergunta}
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
              </summary>
              <div className="px-5 pb-5 text-sm leading-relaxed text-muted-foreground">{item.resposta}</div>
            </details>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="border-t border-border bg-muted py-12 md:py-16">
        <div className="container mx-auto max-w-screen-lg px-4">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <h2 className="mb-2 text-lg font-semibold text-foreground">Não encontrou sua resposta?</h2>
              <p className="text-sm text-muted-foreground">Fale com a nossa equipe — respondemos rapidamente.</p>
            </div>
            <Button
              variant="unstyled"
              asChild
              className="bg-gradient-to-br from-primary to-[var(--color-primary-dark)] px-7 text-white shadow hover:opacity-90"
            >
              <Link to="/contato">Falar com a equipe</Link>
            </Button>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

export default FaqPage;
